import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, Boxes, CircleCheck, RefreshCw, Smartphone, TrendingUp } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { getApp, getVersionStatistics, listChannels, listDeviceMappings } from '../api/AppOtaApi';
import { appBasePath } from '../components/layout/DeploymentWorkspaceNav';
import { Toast } from '../components/ui';
import styles from './AppUpdateOverview.module.scss';

function AppUpdateOverview() {
  const { packageName } = useParams();
  const [app, setApp] = useState(null);
  const [channels, setChannels] = useState([]);
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({ total_devices: 0, version_distribution: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [appResponse, channelResponse, deviceResponse, statsResponse] = await Promise.all([
        getApp(packageName),
        listChannels(packageName),
        listDeviceMappings(packageName),
        getVersionStatistics(packageName),
      ]);
      setApp(appResponse.data || null);
      setChannels(channelResponse.data || []);
      setDevices(deviceResponse.data || []);
      setStats(statsResponse.data || { total_devices: 0, version_distribution: [] });
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message);
    } finally {
      setLoading(false);
    }
  }, [packageName]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const latestVersion = useMemo(() => {
    if (!stats.version_distribution?.length) {
      return null;
    }
    return Math.max(...stats.version_distribution.map(item => Number(item.version) || 0));
  }, [stats.version_distribution]);

  const devicesOnLatest = useMemo(
    () =>
      stats.version_distribution?.find(item => Number(item.version) === latestVersion)
        ?.device_count || 0,
    [latestVersion, stats.version_distribution]
  );
  const adoption = stats.total_devices
    ? Math.round((devicesOnLatest / stats.total_devices) * 100)
    : 0;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>App deployment</span>
          <h1>{app?.display_name || packageName}</h1>
          <p>
            <code className={styles.packageName}>{packageName}</code>
            {app?.is_default ? ' · default app for clients that do not send a package name' : ''}
          </p>
          <p>See the current rollout state, then continue with the task that needs attention.</p>
        </div>
        <button
          type="button"
          className={styles.refreshButton}
          onClick={loadOverview}
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? styles.spinning : ''} />
          {loading ? 'Refreshing' : 'Refresh'}
        </button>
      </header>

      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}

      <section className={styles.metrics} aria-label="Deployment summary">
        <article>
          <span>
            <Boxes size={17} /> Release channels
          </span>
          <strong>{channels.length}</strong>
          <small>Each channel has an independent target version.</small>
        </article>
        <article>
          <span>
            <Smartphone size={17} /> Explicit assignments
          </span>
          <strong>{devices.length}</strong>
          <small>Other devices follow the release channel automatically.</small>
        </article>
        <article>
          <span>
            <TrendingUp size={17} /> Latest-version adoption
          </span>
          <strong>{stats.total_devices ? `${adoption}%` : '—'}</strong>
          <small>
            {latestVersion
              ? `${devicesOnLatest} of ${stats.total_devices} devices on v${latestVersion}`
              : 'No check-ins yet.'}
          </small>
        </article>
      </section>

      <div className={styles.workspaceGrid}>
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <span>Rollout targets</span>
              <h2>Channel status</h2>
            </div>
            <Link to={`${appBasePath(packageName)}/channels`}>
              Manage channels <ArrowRight size={15} />
            </Link>
          </div>
          <div className={styles.channelList}>
            {channels.length === 0 && !loading ? (
              <p className={styles.empty}>No channels configured.</p>
            ) : (
              channels.map(channel => (
                <div className={styles.channelRow} key={channel.channel_name}>
                  <span className={styles.channelIcon}>
                    <CircleCheck size={16} />
                  </span>
                  <div>
                    <strong>{channel.channel_name}</strong>
                    <small>Target application version</small>
                  </div>
                  <b>v{channel.latest_versioncode}</b>
                </div>
              ))
            )}
          </div>
        </section>

        <aside className={styles.nextSteps}>
          <span className={styles.eyebrow}>Choose a task</span>
          <h2>What do you want to change?</h2>
          <Link to={`${appBasePath(packageName)}/channels`}>
            <Boxes size={18} />
            <span>
              <strong>Prepare a release</strong>
              <small>Upload an APK, set channel targets, or generate a patch.</small>
            </span>
            <ArrowRight size={16} />
          </Link>
          <Link to={`${appBasePath(packageName)}/devices`}>
            <Smartphone size={18} />
            <span>
              <strong>Assign test devices</strong>
              <small>Override the default channel for specific devices.</small>
            </span>
            <ArrowRight size={16} />
          </Link>
          <Link to={`${appBasePath(packageName)}/statistics`}>
            <TrendingUp size={18} />
            <span>
              <strong>Review adoption</strong>
              <small>Find devices still running older app versions.</small>
            </span>
            <ArrowRight size={16} />
          </Link>
        </aside>
      </div>
    </div>
  );
}

export default AppUpdateOverview;
